import {computed} from "vue";
import {storeToRefs} from "pinia";
import {mutate} from "swrv";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import {useSystemStore} from "@/stores/system.store";
import {healthcenterApi} from "@/container";
import {HealthcenterOpening} from "@/core/interfaces/healthcenter/Healthcenter";

const openingKey = (healthcenterId: number) => cacheKey(`healthcenters/${healthcenterId}/opening`);

/**
 * The opening sheet of a pharmacy, out of its full record: the only field
 * the agenda needs from it.
 */
export const fetchOpening = (healthcenterId: number): Promise<HealthcenterOpening | null> => {
    return healthcenterApi().read(healthcenterId).then((healthcenter) => healthcenter.openings?.[0] ?? null);
};

/**
 * Composable to read the opening sheet of the user's pharmacy, for the
 * agenda to grey its closing hours and the planning to show them. Null while
 * loading, and for a pharmacy that never filled its hours.
 *
 * Cached by pharmacy under the default SWR policy: the sheet changes a few
 * times a year, from the hours dialog, which refreshes this entry itself.
 */
const useHealthcenterOpening = () => {
    const {environment} = storeToRefs(useSystemStore());
    const healthcenterId = computed(() => environment.value?.entityId ?? null);

    const {data, isLoading: swrLoading, error, mutate: revalidate} = useSwr<HealthcenterOpening | null>(
        () => healthcenterId.value ? openingKey(healthcenterId.value) : null,
        () => healthcenterId.value ? fetchOpening(healthcenterId.value) : Promise.resolve(null)
    );

    const opening = computed<HealthcenterOpening | null>(() => data.value ?? null);

    /**
     * Loading also covers the wait for the environment, after a full reload:
     * no request is made yet, and a screen would read « never filled ».
     */
    const isLoading = computed<boolean>(() => !healthcenterId.value || swrLoading.value);

    /** Read the sheet again, after a failed load. */
    const reload = (): Promise<void> => revalidate();

    return {
        healthcenterId,
        opening,
        isLoading,
        error,
        reload
    };
};

/**
 * Reload the cached sheet of a pharmacy, once its hours were saved, so the
 * agenda and the planning show the new hours rather than the old ones.
 * Resolves with the sheet read back, rejects when that read fails.
 */
export const refreshHealthcenterOpening = async (healthcenterId: number): Promise<HealthcenterOpening | null> => {
    const opening = await fetchOpening(healthcenterId);

    await mutate(openingKey(healthcenterId), opening);

    return opening;
};

export default useHealthcenterOpening;
